import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { QuillEditorHelper } from '../../../../helpers/quill-editor.helper';
import { SpinnerHelper } from '../../../../helpers/spinner.helper';
import { ToastrHelper } from '../../../../helpers/toastr.helper';
import { Project, PROJECT_MOTIVATION_MAX_LENGTH } from '../../../../models/projects/project.model';
import { ProjectsService } from '../../../../services/projects/projects.service';

@Component({
    selector: 'dialog-join-project',
    template: `
        <p-dialog
            header="{{ 'projects.modals.join.title' | translate }}"
            [(visible)]="visible"
            [modal]="true"
            [draggable]="false"
            [resizable]="false"
        >
            <form [formGroup]="joinForm" novalidate>
                <p>
                    {{ 'projects.modals.join.message' | translate }}
                </p>
                <quill-editor
                    [placeholder]="'projects.modals.join.motivationPlaceholder' | translate"
                    theme="snow"
                    required="true"
                    [modules]="quillEditorHelper.modulesWithoutImage"
                    [formats]="quillEditorHelper.formatsWithoutImage"
                    [formControl]="joinForm.controls.motivation"
                    (onContentChanged)="changeMotivation($event.editor)"
                    class="notranslate"
                >
                </quill-editor>
                <div class="p-d-fkw">
                    <small class="p-mr-auto p-my-auto d-inline-flex">
                        <div
                            [ngClass]="{
                                'text-danger': motivationCharLength >= PROJECT_MOTIVATION_MAX_LENGTH
                            }"
                        >
                            {{ motivationCharLength }}
                        </div>
                        <div>/</div>
                        <div>
                            {{ 'projects.modals.join.length' | translate : { maxLength: PROJECT_MOTIVATION_MAX_LENGTH } }}
                        </div>
                    </small>
                </div>

                <div
                    *ngIf="joinForm.controls.motivation.invalid && (joinForm.controls.motivation.dirty || joinForm.controls.motivation.touched)"
                    class="alert alert-danger"
                >
                    <div *ngIf="joinForm.controls.motivation.errors.required">
                        {{ 'projects.modals.join.motivationRequired' | translate }}
                    </div>
                    <div *ngIf="joinForm.controls.motivation.errors.maxlength">
                        {{ 'projects.modals.join.motivationMaxLength' | translate : { maxLength: 2500 } }}
                    </div>
                </div>
            </form>
            <p-footer>
                <button pButton class="p-button-text" [label]="'projects.modals.join.cancel' | translate" (click)="closeModal()"></button>

                <button
                    pButton
                    [disabled]="joinForm.invalid"
                    [label]="'projects.modals.join.confirm' | translate"
                    (click)="joinThisProject()"
                ></button>
            </p-footer>
        </p-dialog>
    `,
})
export class DialogJoinProjectComponent {
    public PROJECT_MOTIVATION_MAX_LENGTH = PROJECT_MOTIVATION_MAX_LENGTH;
    @Output() public acted = new EventEmitter<any>();
    public attendeeMotivation: string;
    public joinForm = this.formBuilder.group({
        motivation: [null, []],
    });
    public motivationCharLength = 0;
    @Input() public project: Project;
    @Input() public visible: boolean;
    @Output() public visibleChange = new EventEmitter<boolean>();

    constructor(
        public readonly quillEditorHelper: QuillEditorHelper,
        private readonly formBuilder: FormBuilder,
        private readonly modalService: NgbModal,
        private readonly spinnerHelper: SpinnerHelper,
        private readonly projectsService: ProjectsService,
        private readonly translate: TranslateService,
        private readonly toastrHelper: ToastrHelper,
    ) {}

    public changeMotivation(editor: any) {
        this.motivationCharLength = this.quillEditorHelper.getPlainTextLength(editor.container.innerHTML);

        this.quillEditorHelper.setQuillControlError(this.joinForm.controls.motivation, this.motivationCharLength, PROJECT_MOTIVATION_MAX_LENGTH);
    }

    public closeModal() {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    public joinThisProject() {
        if (this.joinForm.invalid) return;

        this.spinnerHelper.showSpinner();
        const command: Project = this.joinForm.value;
        const str = command.motivation;
        const parser = new DOMParser();
        const doc = parser.parseFromString(str, 'text/html');
        this.attendeeMotivation = doc.body.innerHTML;
        this.projectsService.joinProject(this.project.id, this.attendeeMotivation).subscribe(
            () => {
                const msg = this.translate.instant('projects.modals.join.toast.success');
                this.toastrHelper.showSuccess(msg);
                this.acted.emit();
            },
            (error) => {
                this.toastrHelper.showGenericError(error);
            },
            () => {
                this.spinnerHelper.hideSpinner();
                this.modalService.dismissAll();
                this.closeModal();
            },
        );
    }
}
